import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import { getTenants, deleteTenant } from "../../actions/tenant";
import TenantForm from "./TenantForm";
import { sinceDay } from "./tenantLabels";
import { dateText } from "../users/roleLabels";
import { primaryButton, smallButton } from "../template/templateLabels";

// The tenants who rent kiosks, and which kiosks from which day. A tenant only
// reads the report of their own kiosks (the backend's routes/api/tenant.js);
// they set their own password from a link mailed to them, and are pending until
// they do.

const dangerButton =
  "inline-flex items-center px-3 py-1 text-xs font-medium leading-5 text-red-600 transition-colors duration-150 bg-white border border-red-200 rounded-lg dark:text-red-400 dark:bg-gray-700 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-gray-600 focus:outline-none";

// null: the form is closed; NEW: adding a tenant; otherwise the tenant being edited
const NEW = "new";

const Tenants = ({ getTenants, deleteTenant, tenant: { tenants, printers, loading } }) => {
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    getTenants();
  }, [getTenants]);

  const printerName = (printerId) => {
    const printer = printers.find((item) => item._id === printerId);
    return printer ? printer.name : "Устгагдсан киоск";
  };

  const remove = async (item) => {
    if (
      !window.confirm(
        `"${item.name}" түрээслэгчийг устгах уу? Тэр даруй системээс гарна.`
      )
    )
      return;
    setBusyId(item._id);
    await deleteTenant(item._id);
    setBusyId(null);
  };

  const row = (item) => {
    const busy = busyId === item._id;
    return (
      <tr key={item._id} className="text-gray-700 dark:text-gray-400">
        <td className="px-4 py-3 text-sm">
          <div className="flex items-center">
            <div
              className="flex items-center justify-center flex-shrink-0 w-8 h-8 mr-3 text-sm font-semibold text-purple-600 uppercase bg-purple-100 rounded-full dark:text-purple-300 dark:bg-purple-900"
              aria-hidden="true"
            >
              {(item.name || "?").trim().charAt(0)}
            </div>
            <span className="font-semibold">{item.name}</span>
            {item.pending && (
              <span
                className="px-2 py-0.5 ml-2 text-xs text-yellow-700 bg-yellow-100 rounded-full dark:text-yellow-100 dark:bg-yellow-700"
                title="Урилгаа нээж нууц үгээ үүсгээгүй. Үүсгэх хүртэл нэвтэрч чадахгүй."
              >
                Баталгаажаагүй
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-sm">{item.email}</td>
        <td className="px-4 py-3 text-sm whitespace-normal">
          <div className="flex flex-wrap gap-1">
            {item.printers.map((kiosk) => (
              <span
                key={kiosk.printerId}
                className="px-2 py-0.5 text-xs text-gray-600 bg-gray-100 rounded dark:text-gray-300 dark:bg-gray-700"
              >
                {printerName(kiosk.printerId)}
                <span className="text-gray-400">
                  {" · "}
                  {sinceDay(kiosk.since) ? `${sinceDay(kiosk.since)}-с` : "бүх хугацаа"}
                </span>
              </span>
            ))}
          </div>
        </td>
        <td className="px-4 py-3 text-sm">{dateText(item.date)}</td>
        <td className="px-4 py-3 text-sm">
          <div className="flex justify-end gap-2">
            <button
              className={dangerButton + (busy ? " opacity-50 cursor-wait" : "")}
              disabled={busy}
              onClick={() => remove(item)}
            >
              <i className="mr-1.5 fas fa-trash-alt" />
              Устгах
            </button>
            <button
              className={smallButton + " inline-flex items-center" + (busy ? " opacity-50 cursor-wait" : "")}
              disabled={busy}
              onClick={() => setEditing(item)}
            >
              <i className="mr-1.5 fas fa-pen" />
              Засах
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <Admin>
      {/* min-w-0: the layout's grid would otherwise widen the page to the
          table's width on a phone, instead of letting it scroll */}
      <div className="min-w-0">
        <div className="flex flex-wrap items-end justify-between gap-4 my-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-200">
              Түрээслэгч
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Киоск түрээслэгчид зөвхөн өөрт оноосон киоскуудын орлого, зарлага,
              хэвлэлтийн тайланг харна
            </p>
          </div>
          <button
            className={primaryButton + " inline-flex items-center whitespace-nowrap"}
            onClick={() => setEditing(NEW)}
            disabled={loading}
          >
            <i className="mr-2 fas fa-plus" />
            Шинэ түрээслэгч
          </button>
        </div>

        {loading ? (
          <img
            src={spinner}
            className="my-6"
            style={{ width: "50px", margin: "auto", display: "block" }}
            alt="Loading..."
          />
        ) : tenants.length === 0 ? (
          <p className="py-10 text-sm text-center text-gray-500 dark:text-gray-400">
            Түрээслэгч бүртгэгдээгүй байна.
          </p>
        ) : (
          <div className="w-full mb-8 overflow-hidden rounded-lg shadow-xs">
            <div className="w-full overflow-x-auto">
              <table className="w-full whitespace-nowrap">
                <thead>
                  <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b dark:border-gray-700 bg-gray-50 dark:text-gray-400 dark:bg-gray-800">
                    <th className="px-4 py-3">Нэр</th>
                    <th className="px-4 py-3">Имэйл</th>
                    <th className="px-4 py-3">Киоскууд</th>
                    <th className="px-4 py-3">Бүртгэсэн</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y dark:divide-gray-700 dark:bg-gray-800">
                  {tenants.map(row)}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {editing && (
          <TenantForm
            tenant={editing === NEW ? null : editing}
            printers={printers}
            tenants={tenants}
            onClose={() => setEditing(null)}
          />
        )}
      </div>
    </Admin>
  );
};

Tenants.propTypes = {
  getTenants: PropTypes.func.isRequired,
  deleteTenant: PropTypes.func.isRequired,
  tenant: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  tenant: state.tenant,
});

export default connect(mapStateToProps, { getTenants, deleteTenant })(Tenants);
